import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Snackbar,
  Chip,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Edit,
  GetApp,
  Print,
  AutoAwesome,
  Send,
  ChatBubbleOutline,
  Share as ShareIcon,
} from '@mui/icons-material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import PsychologyOutlinedIcon from '@mui/icons-material/PsychologyOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import { useAuth } from '../../contexts/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import { shareProfileLink } from '../../utils/shareLink';
import AIBidGenerator from './AIBidGenerator';
import DigitalSignatureCapture from '../signatures/DigitalSignatureCapture';
import signatureService from '../../services/signatureService';
import contractService from '../../services/contractService';
import AIContractGenerator from '../contracts/AIContractGenerator';
import ContractViewer from '../contracts/ContractViewer';
import DocumentFooter from '../documents/DocumentFooter';
import DialogCloseButton from '../common/DialogCloseButton';
import { QuoteNegotiationActions } from '../QuoteAffordability';
import {
  renderMarkdown,
  SectionWrapper,
  SectionHeader,
  FieldLabel,
  DOCUMENT_COLORS,
} from '../documents/documentStyles';

/**
 * Bid Detail View Component
 *
 * Displays complete bid with branded header (matching Inspection Report style)
 * Includes client approval actions
 */
// Parse a date-only value ('YYYY-MM-DD' or ISO) as LOCAL time — bare date
// strings are treated as UTC by new Date() and render a day early in
// negative-offset timezones (e.g. the US diaspora).
const parseLocalDate = (value) => {
  if (!value) return null;
  const [y, m, d] = String(value).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const formatLocalDate = (value) => {
  const date = parseLocalDate(value);
  return date ? date.toLocaleDateString() : null;
};

// Add calendar units to a local date, clamping month-end overflow
// (Jan 31 + 1 month → Feb 28/29, not Mar 3).
const addDuration = (start, n, unit) => {
  const end = new Date(start.getTime());
  if (unit === 'months') {
    const day = end.getDate();
    end.setDate(1);
    end.setMonth(end.getMonth() + n);
    const lastDay = new Date(end.getFullYear(), end.getMonth() + 1, 0).getDate();
    end.setDate(Math.min(day, lastDay));
  } else {
    end.setDate(end.getDate() + n * (unit === 'days' ? 1 : 7));
  }
  return end;
};

const BidDetailView = ({ bidId: propBidId, onUpdate }) => {
  const { bidId: paramBidId } = useParams();
  const bidId = propBidId || paramBidId;
  const { getApiClient, user } = useAuth();
  const navigate = useNavigate();

  const [bid, setBid] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);

  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [postingComment, setPostingComment] = useState(false);

  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [changesDialogOpen, setChangesDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [requestedChanges, setRequestedChanges] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [approvalSuccess, setApprovalSuccess] = useState(false);
  const [approvedProjectId, setApprovedProjectId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Formal contract state (accepted bids only). Fetched lazily; 403/404 tolerated.
  const [contracts, setContracts] = useState([]);
  const [showContractGenerator, setShowContractGenerator] = useState(false);
  const [contractViewerOpen, setContractViewerOpen] = useState(false);
  const [activeContract, setActiveContract] = useState(null);

  const ensureArray = (data) => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (typeof data === 'string') {
      try {
        const parsed = JSON.parse(data);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        return [];
      }
    }
    return [];
  };

  useEffect(() => {
    if (bidId) {
      loadBid();
    }
  }, [bidId]);

  const loadBid = async () => {
    setLoading(true);
    setError(null);
    try {
      const apiClient = getApiClient();
      const response = await apiClient.get(`/bids/${bidId}`);
      const loadedBid = response.data.data;
      setBid(loadedBid);
      loadComments();
      if (loadedBid?.status === 'accepted') {
        loadContracts();
      }
    } catch (error) {
      console.error('Error loading bid:', error);
      setError(error.response?.data?.error || 'Failed to load bid');
    } finally {
      setLoading(false);
    }
  };

  const loadComments = async () => {
    try {
      const apiClient = getApiClient();
      const response = await apiClient.get(`/bids/${bidId}/comments`);
      setComments(Array.isArray(response.data.data) ? response.data.data : []);
    } catch (error) {
      console.error('Error loading bid comments:', error);
    }
  };

  const handlePostComment = async () => {
    if (!newComment.trim()) return;
    setPostingComment(true);
    try {
      const apiClient = getApiClient();
      await apiClient.post(`/bids/${bidId}/comments`, { commentText: newComment.trim() });
      setNewComment('');
      await loadComments();
    } catch (error) {
      console.error('Error posting bid comment:', error);
      setSnackbar({ open: true, message: error.response?.data?.error || 'Failed to post your question', severity: 'error' });
    } finally {
      setPostingComment(false);
    }
  };

  const handleMessageOtherParty = () => {
    const otherPartyId = String(user?.id) === String(bid?.contractor_id) ? bid?.client_id : bid?.contractor_id;
    if (!otherPartyId) return;
    const params = new URLSearchParams({ userId: otherPartyId });
    if (bid?.project_id) params.set('projectId', bid.project_id);
    navigate(`/messages?${params.toString()}`);
  };

  // Contractor shares the quotation into WhatsApp/etc. The /share/ URL renders
  // a deliberately generic link preview (no amounts, no names) and sends the
  // recipient to sign in and review — the quotation itself stays behind auth.
  const handleShareQuotationLink = async () => {
    const result = await shareProfileLink({
      path: `/share/quote/${bid.id}`,
      title: 'Quotation on Eqaya'
    });
    if (result === 'copied') {
      setSnackbar({ open: true, message: 'Quotation link copied — paste it to your client', severity: 'success' });
    }
  };

  const handleSubmitRevisedBid = async () => {
    setActionLoading(true);
    try {
      const apiClient = getApiClient();
      await apiClient.post(`/bids/${bidId}/submit`);
      setSnackbar({ open: true, message: 'Revised quotation submitted to client!', severity: 'success' });
      loadBid();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error submitting revised bid:', error);
      setSnackbar({ open: true, message: error.response?.data?.error || 'Failed to submit the quotation', severity: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  // Client accepts by SIGNING: capture a signature, then accept-with-signature
  // (the signature-less /approve path is no longer used from the client UI).
  const handleAcceptWithSignature = async (signaturePayload) => {
    setActionLoading(true);
    try {
      const signatureResponse = await signatureService.createSignature(signaturePayload);
      const signatureId = signatureResponse?.signature?.id;
      const apiClient = getApiClient();
      const response = await apiClient.post(`/bids/${bidId}/accept-with-signature`, { signatureId });
      setSignatureModalOpen(false);
      setApprovalSuccess(true);
      if (response.data.projectId) {
        setApprovedProjectId(response.data.projectId);
      }
      setSnackbar({
        open: true,
        message: 'Quotation accepted and signed! Project and work stages are ready for review.',
        severity: 'success',
      });
      if (onUpdate) onUpdate(response.data.data);
      // Refresh bid to update status (hides approve button via canApprove logic)
      await loadBid();
    } catch (error) {
      console.error('Error accepting bid:', error);
      setSignatureModalOpen(false);
      const serverMsg = error.response?.data?.error || '';
      let userMessage = 'Failed to accept the quotation. ';
      if (serverMsg.includes('wallet') || serverMsg.includes('escrow') || serverMsg.includes('fund')) {
        userMessage += 'Insufficient escrow funds — please fund your project wallet first, then retry.';
      } else if (serverMsg.includes('expired')) {
        userMessage += 'This quotation has expired. Ask the contractor to resubmit with updated pricing.';
      } else if (serverMsg.includes('status') || serverMsg.includes('already')) {
        userMessage += 'This quotation may have already been accepted or is no longer in a reviewable state. Refresh the page to see the latest status.';
      } else if (error.response?.status === 403) {
        userMessage += 'You do not have permission to accept this quotation. Only the project client can accept.';
      } else if (error.response?.status === 404) {
        userMessage += 'Quotation not found. It may have been removed.';
      } else {
        userMessage += serverMsg || 'An unexpected error occurred. Please try again or contact support.';
      }
      setSnackbar({ open: true, message: userMessage, severity: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  // Fetch formal contract versions for an accepted bid. Party-only on the
  // backend, so 403/404 are expected for non-parties — swallow them.
  const loadContracts = async () => {
    try {
      const list = await contractService.listForBid(bidId);
      setContracts(Array.isArray(list) ? list : []);
    } catch (error) {
      // non-party or none yet — leave contracts empty
      setContracts([]);
    }
  };

  // Download the signed quotation PDF (the accepted-quote document, distinct from
  // the formal contract). Only meaningful once the client has signed.
  const handleDownloadSignedQuotation = async () => {
    try {
      const apiClient = getApiClient();
      const response = await apiClient.get(`/bids/${bidId}/download-contract`, {
        responseType: 'blob',
        timeout: 120000,
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `eqaya-quotation-${bid?.bid_number || bidId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading signed quotation:', error);
      setSnackbar({ open: true, message: 'Failed to download the signed quotation. Please try again.', severity: 'error' });
    }
  };

  const handleRejectBid = async () => {
    if (!rejectionReason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }
    setActionLoading(true);
    try {
      const apiClient = getApiClient();
      const response = await apiClient.post(`/bids/${bidId}/reject`, { reason: rejectionReason });
      alert('Bid rejected');
      setRejectDialogOpen(false);
      setRejectionReason('');
      if (onUpdate) onUpdate(response.data.data);
      else loadBid();
    } catch (error) {
      console.error('Error rejecting bid:', error);
      alert(error.response?.data?.error || 'Failed to reject bid');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRequestChanges = async () => {
    if (!requestedChanges.trim()) {
      alert('Please describe the changes you would like');
      return;
    }
    setActionLoading(true);
    try {
      const apiClient = getApiClient();
      const response = await apiClient.post(`/bids/${bidId}/request-changes`, { requestedChanges });
      alert('Changes requested successfully');
      setChangesDialogOpen(false);
      setRequestedChanges('');
      if (onUpdate) onUpdate(response.data.data);
      else loadBid();
    } catch (error) {
      console.error('Error requesting changes:', error);
      alert(error.response?.data?.error || 'Failed to request changes');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const apiClient = getApiClient();
      const response = await apiClient.get(`/bids/${bidId}/download`, {
        responseType: 'blob',
        timeout: 120000,
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `eqaya-bid-${bid?.bid_number || bidId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading bid PDF:', error);
      alert('Failed to download PDF. Please try again.');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const isClient = user?.role === 'client' && user?.id === bid?.client_id;
  const isContractor = user?.role === 'contractor' && user?.id === bid?.contractor_id;
  const targetProjectId = approvedProjectId || bid?.created_project_id || bid?.project_id;
  const canApprove = isClient && ['submitted', 'under_review'].includes(bid?.status);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!bid) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="warning">Bid not found</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Action Buttons (No Print) */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }} className="no-print">
        <Typography variant="h5">Quotation Preview</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {/* A submitted quotation can be revised too — mainly to add payment
              stages so the client is asked for the first stage, not the total.
              The wizard re-submits, which re-notifies the client. */}
          {user?.id === bid.contractor_id && ['draft', 'changes_requested', 'submitted', 'under_review'].includes(bid.status) && (
            <Button variant="outlined" startIcon={<Edit />} onClick={() => setShowEditModal(true)}
              color={bid.status === 'changes_requested' ? 'warning' : 'primary'}
            >
              {bid.status === 'draft' ? 'Edit Quotation' : 'Revise Quotation'}
            </Button>
          )}
          {user?.id === bid.contractor_id && ['draft', 'changes_requested'].includes(bid.status) && (
            <Button
              variant="contained"
              startIcon={<Send />}
              onClick={handleSubmitRevisedBid}
              disabled={actionLoading}
              sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}
            >
              {bid.status === 'changes_requested' ? 'Submit Revised Quotation' : 'Submit to Client'}
            </Button>
          )}
          {(String(user?.id) === String(bid.contractor_id) || String(user?.id) === String(bid.client_id)) && (
            <Button
              variant="outlined"
              startIcon={<ChatBubbleOutline />}
              onClick={handleMessageOtherParty}
            >
              {String(user?.id) === String(bid.contractor_id) ? 'Message Client' : 'Message Contractor'}
            </Button>
          )}
          {String(user?.id) === String(bid.contractor_id) && !['draft'].includes(bid.status) && (
            <Button
              variant="outlined"
              startIcon={<ShareIcon />}
              onClick={handleShareQuotationLink}
            >
              Share Quotation Link
            </Button>
          )}
          {canApprove && (
            <Button
              variant="outlined"
              startIcon={<AutoAwesome />}
              sx={{ borderColor: DOCUMENT_COLORS.brand, color: DOCUMENT_COLORS.brand }}
            >
              AI Recommendations
            </Button>
          )}
          <Button variant="outlined" startIcon={<GetApp />} onClick={handleDownloadPDF}>
            Download PDF
          </Button>
          <Button variant="outlined" startIcon={<Print />} onClick={handlePrint}>
            Print
          </Button>
        </Box>
      </Box>

      {/* The number a client can act on: first stage, not the lump sum. */}
      {isClient && ['submitted', 'under_review', 'changes_requested'].includes(bid.status) && (() => {
        const schedule = ensureArray(bid.payment_schedule).filter((s) => s && s.name && parseFloat(s.amount) > 0);
        const first = schedule[0];
        const cur = bid.currency || 'USD';
        const money = (n) => `${cur} ${Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
        return (
          <Alert severity={schedule.length > 1 ? 'success' : 'info'} icon={<AccountBalanceWalletOutlinedIcon fontSize="inherit" />} className="no-print" sx={{ mb: 3 }}>
            {schedule.length > 1 ? (
              <>
                <strong>Start with {money(first.amount)} for stage 1 ("{first.name}")</strong> — not the whole {money(bid.total_cost)} at once.
                {' '}This quotation is in {schedule.length} stages. Each stage is funded into your project wallet only when it's about to start, and paid out only when you approve the finished work.
              </>
            ) : (
              <>
                <strong>You don't have to fund {money(bid.total_cost)} at once.</strong> Ask {bid.contractor_name || 'the contractor'} to split this into stages you fund one at a time, or tell them what you have available and they'll quote what it covers.
              </>
            )}
            <QuoteNegotiationActions kind="bid" id={bid.id} total={bid.total_cost} currency={cur} sx={{ mt: 1.5 }} />
          </Alert>
        );
      })()}

      {/* Main Content Wrapper for PDF Export */}
      <div className="bid-detail-content">
        {/* Branded Header */}
        <Paper
          elevation={0}
          sx={{
            background: `linear-gradient(135deg, ${DOCUMENT_COLORS.brandDark} 0%, ${DOCUMENT_COLORS.brand} 100%)`,
            color: 'white',
            p: 5,
            borderRadius: 2,
            mb: 4,
          }}
        >
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 3 }}>
            QUOTATION
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ opacity: 0.8, textTransform: 'uppercase', letterSpacing: 1 }}>
                  Project
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {bid.project_name || 'Untitled Project'}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ opacity: 0.8, textTransform: 'uppercase', letterSpacing: 1 }}>
                  Location
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {bid.project_location || 'Zimbabwe'}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ opacity: 0.8, textTransform: 'uppercase', letterSpacing: 1 }}>
                  Quotation Date
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {new Date(bid.created_at).toLocaleDateString()}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ opacity: 0.8, textTransform: 'uppercase', letterSpacing: 1 }}>
                  Quotation ID
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {bid.bid_number || bid.id}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.8, textTransform: 'uppercase', letterSpacing: 1 }}>
                  Contractor
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {bid.contractor_name || 'N/A'}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.8, textTransform: 'uppercase', letterSpacing: 1 }}>
                  Status
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {bid.status.replace('_', ' ').toUpperCase()}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Changes Requested Banner */}
        {bid.status === 'changes_requested' && bid.client_conditions && (
          <Alert
            severity="warning"
            sx={{
              mb: 3,
              '& .MuiAlert-message': { width: '100%' }
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
              Changes Requested by Client
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {bid.client_conditions}
            </Typography>
            {bid.changes_requested_at && (
              <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.7 }}>
                Requested on {new Date(bid.changes_requested_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </Typography>
            )}
          </Alert>
        )}

        {/* Contractor's own uploaded quotation document */}
        {bid.quote_source === 'uploaded' && (
          <SectionWrapper>
            <SectionHeader
              icon={<DescriptionOutlinedIcon fontSize="small" />}
              title="Quotation Document"
            />
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                {bid.contractor_name || 'The contractor'} prepared this quotation on their own letterhead.
                The confirmed total of <strong>{bid.currency || 'USD'} {parseFloat(bid.total_cost).toLocaleString()}</strong> is
                what you accept and what Eqaya payments are based on. Accepted this way, it is eligible for
                the Eqaya project wallet, acceptance record, stage tracking, and dispute support.
              </Typography>
            </Alert>
            <Button
              variant="contained"
              startIcon={<GetApp />}
              onClick={async () => {
                try {
                  const apiClient = getApiClient();
                  const res = await apiClient.get(`/bids/${bidId}/quotation-document`);
                  window.open(res.data.data.url, '_blank', 'noopener');
                } catch (err) {
                  setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to open the quotation document', severity: 'error' });
                }
              }}
            >
              View Quotation Document
            </Button>
            {bid.valid_until && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                Valid until {new Date(bid.valid_until).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </Typography>
            )}
          </SectionWrapper>
        )}

        {/* Executive Summary */}
        {bid.executive_summary && (
          <SectionWrapper>
            <SectionHeader
              icon={<DescriptionOutlinedIcon fontSize="small" />}
              title="Executive Summary"
            />
            <Box sx={{ lineHeight: 1.8, color: '#555' }}>
              {renderMarkdown(bid.executive_summary)}
            </Box>
          </SectionWrapper>
        )}

        {/* Project Understanding */}
        {bid.project_understanding && (
          <SectionWrapper>
            <SectionHeader
              icon={<PsychologyOutlinedIcon fontSize="small" />}
              title="Project Understanding"
            />
            <Box sx={{ lineHeight: 1.8, color: '#555' }}>
              {renderMarkdown(bid.project_understanding)}
            </Box>
          </SectionWrapper>
        )}

        {/* Scope of Work */}
        <SectionWrapper>
          <SectionHeader
            icon={<BuildOutlinedIcon fontSize="small" />}
            title="Scope of Work"
          />
          <Box sx={{ lineHeight: 1.8, color: '#555' }}>
            {renderMarkdown(bid.scope_of_work)}
          </Box>
        </SectionWrapper>

        {/* Inclusions, Exclusions, Assumptions */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {ensureArray(bid.inclusions).length > 0 && (
            <Grid item xs={12} md={4}>
              <Paper
                elevation={0}
                sx={{ p: 3, background: '#e8f5e9', border: '2px solid #4caf50', borderRadius: 2 }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#2e7d32', mb: 2 }}>
                  {"\u2713"} Inclusions
                </Typography>
                <Box component="ul" sx={{ pl: 2, m: 0 }}>
                  {ensureArray(bid.inclusions).map((item, index) => (
                    <Typography component="li" key={index} variant="body2" sx={{ mb: 1 }}>
                      {item}
                    </Typography>
                  ))}
                </Box>
              </Paper>
            </Grid>
          )}

          {ensureArray(bid.exclusions).length > 0 && (
            <Grid item xs={12} md={4}>
              <Paper
                elevation={0}
                sx={{ p: 3, background: '#ffebee', border: '2px solid #f44336', borderRadius: 2 }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#c62828', mb: 2 }}>
                  {"\u2717"} Exclusions
                </Typography>
                <Box component="ul" sx={{ pl: 2, m: 0 }}>
                  {ensureArray(bid.exclusions).map((item, index) => (
                    <Typography component="li" key={index} variant="body2" sx={{ mb: 1 }}>
                      {item}
                    </Typography>
                  ))}
                </Box>
              </Paper>
            </Grid>
          )}

          {ensureArray(bid.assumptions).length > 0 && (
            <Grid item xs={12} md={4}>
              <Paper
                elevation={0}
                sx={{ p: 3, background: DOCUMENT_COLORS.brandLight, border: `2px solid ${DOCUMENT_COLORS.brand}`, borderRadius: 2 }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, color: DOCUMENT_COLORS.brandDark, mb: 2 }}>
                  {"\u24D8"} Assumptions
                </Typography>
                <Box component="ul" sx={{ pl: 2, m: 0 }}>
                  {ensureArray(bid.assumptions).map((item, index) => (
                    <Typography component="li" key={index} variant="body2" sx={{ mb: 1 }}>
                      {item}
                    </Typography>
                  ))}
                </Box>
              </Paper>
            </Grid>
          )}
        </Grid>

        {/* Cost Breakdown. For an uploaded PDF the document itself is the
            breakdown; what's stored under cost_breakdown there is the AI
            extraction record, never shown to the client. */}
        {bid.cost_breakdown && bid.quote_source !== 'uploaded' && (
          <SectionWrapper>
            <SectionHeader
              icon={<AccountBalanceWalletOutlinedIcon fontSize="small" />}
              title="Cost Breakdown"
            />
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ background: DOCUMENT_COLORS.brand }}>
                    <TableCell sx={{ fontWeight: 600, color: 'white' }}>Category</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'white' }}>Description</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: 'white' }}>
                      Amount ({bid.currency})
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(() => {
                    let breakdown = bid.cost_breakdown;
                    if (typeof breakdown === 'string') {
                      try { breakdown = JSON.parse(breakdown); } catch (e) { breakdown = []; }
                    }
                    // Itemized BOQ lines live under the reserved lineItems key
                    let lineItems = {};
                    if (breakdown && typeof breakdown === 'object' && !Array.isArray(breakdown)) {
                      lineItems = breakdown.lineItems && typeof breakdown.lineItems === 'object' ? breakdown.lineItems : {};
                      breakdown = Object.entries(breakdown)
                        .filter(([category, amount]) => category !== 'lineItems' && (typeof amount === 'number' || (typeof amount === 'string' && amount.trim() !== '' && !Number.isNaN(Number(amount)))))
                        .map(([category, amount]) => ({ category, description: '', amount }));
                    }
                    if (!Array.isArray(breakdown)) breakdown = [];
                    return breakdown.map((item, index) => {
                      const items = Array.isArray(lineItems[item.category]) ? lineItems[item.category] : [];
                      return (
                        <React.Fragment key={index}>
                          <TableRow>
                            <TableCell sx={{ textTransform: 'capitalize', fontWeight: items.length > 0 ? 600 : 400 }}>{item.category || 'N/A'}</TableCell>
                            <TableCell>{item.description || (items.length > 0 ? `${items.length} itemized line${items.length === 1 ? '' : 's'}` : '')}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: items.length > 0 ? 600 : 400 }}>
                              {parseFloat(item.amount || 0).toLocaleString()}
                            </TableCell>
                          </TableRow>
                          {items.map((lineItem, lineIndex) => (
                            <TableRow key={`${item.category}-${lineIndex}`}>
                              <TableCell sx={{ pl: 4, py: 0.5, color: 'text.secondary', fontSize: '0.85rem', borderBottom: 'none' }} colSpan={2}>
                                {lineItem.description} — {lineItem.quantity} {lineItem.unit}{lineItem.priceAtPurchase ? '' : ` × ${parseFloat(lineItem.unitPrice || 0).toLocaleString()}`}
                              </TableCell>
                              <TableCell align="right" sx={{ py: 0.5, color: 'text.secondary', fontSize: '0.85rem', borderBottom: 'none', fontStyle: lineItem.priceAtPurchase ? 'italic' : 'normal' }}>
                                {lineItem.priceAtPurchase
                                  ? 'Priced at purchase'
                                  : ((parseFloat(lineItem.quantity) || 0) * (parseFloat(lineItem.unitPrice) || 0)).toLocaleString()}
                              </TableCell>
                            </TableRow>
                          ))}
                        </React.Fragment>
                      );
                    });
                  })()}
                  <TableRow sx={{ background: DOCUMENT_COLORS.brandLight }}>
                    <TableCell colSpan={2} sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                      Total Cost
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                      {bid.currency} {parseFloat(bid.total_cost).toLocaleString()}
                    </TableCell>
                  </TableRow>
                  {(() => {
                    let breakdown = bid.cost_breakdown;
                    if (typeof breakdown === 'string') {
                      try { breakdown = JSON.parse(breakdown); } catch (e) { breakdown = {}; }
                    }
                    const hasLineItems = breakdown && typeof breakdown === 'object' && !Array.isArray(breakdown) &&
                      Object.keys(breakdown.lineItems || {}).length > 0;
                    const hasAtPurchase = hasLineItems &&
                      Object.values(breakdown.lineItems).flat().some((lineItem) => lineItem?.priceAtPurchase);
                    return hasLineItems ? (
                      <TableRow>
                        <TableCell colSpan={3} sx={{ borderBottom: 'none', pt: 1.5 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            The itemized quantities above are an estimate based on the site assessment. Actual quantities may be more or less, and additional items may be identified once work begins — any changes are agreed through change orders before extra cost is incurred.
                            {hasAtPurchase && ' Items marked "priced at purchase" are quoted for quantity only because prices change frequently — the actual price is confirmed when the item is bought, and is not included in the total above.'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : null;
                  })()}
                </TableBody>
              </Table>
            </TableContainer>
          </SectionWrapper>
        )}

        {/* Timeline & Payment Schedule */}
        <SectionWrapper>
          <SectionHeader
            icon={<ScheduleOutlinedIcon fontSize="small" />}
            title="Timeline & Payment Schedule"
          />
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <FieldLabel>Estimated Duration</FieldLabel>
              <Typography variant="h6">
                {bid.estimated_duration
                  ? `${bid.estimated_duration} ${bid.duration_unit || 'weeks'}`
                  : '—'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FieldLabel>Start Date</FieldLabel>
              <Typography variant="h6">
                {formatLocalDate(bid.estimated_start_date) || 'TBD'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FieldLabel>Completion Date</FieldLabel>
              <Typography variant="h6">
                {formatLocalDate(bid.estimated_completion_date)
                  || (() => {
                    const start = parseLocalDate(bid.estimated_start_date);
                    const n = parseInt(bid.estimated_duration, 10) || 0;
                    if (!start || !n) return 'TBD';
                    return addDuration(start, n, bid.duration_unit || 'weeks').toLocaleDateString();
                  })()}
              </Typography>
            </Grid>
          </Grid>

          {bid.payment_schedule && (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ background: DOCUMENT_COLORS.brand }}>
                    <TableCell sx={{ fontWeight: 600, color: 'white' }}>Work Stage</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'white' }}>Description</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: 'white' }}>
                      Payment ({bid.currency})
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: 'white' }}>
                      Timeline
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ensureArray(bid.payment_schedule).map((milestone, index) => (
                    <TableRow key={index}>
                      <TableCell>{milestone.name || 'N/A'}</TableCell>
                      <TableCell>{milestone.description || ''}</TableCell>
                      <TableCell align="right">
                        {parseFloat(milestone.amount || 0).toLocaleString()}
                      </TableCell>
                      <TableCell align="right">{milestone.timeline || 'N/A'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {ensureArray(bid.payment_schedule).length > 1 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
              You fund one stage at a time. Each stage's amount goes into your project wallet just before that stage starts, and is released to the contractor only when you approve the finished stage — you never fund more than the current stage.
            </Typography>
          )}
          {ensureArray(bid.payment_schedule).length === 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
              This quotation has no stage breakdown yet. Ask the contractor for one (button above) — you'd then fund one stage at a time into your project wallet, released only when you approve that stage, so you never fund more than the current stage.
            </Typography>
          )}
        </SectionWrapper>

        {/* Terms & Conditions */}
        {bid.terms_and_conditions && (
          <SectionWrapper>
            <SectionHeader
              icon={<GavelOutlinedIcon fontSize="small" />}
              title="Terms & Conditions"
            />
            <Box sx={{ lineHeight: 1.8, color: '#555' }}>
              {renderMarkdown(bid.terms_and_conditions)}
            </Box>
          </SectionWrapper>
        )}

        {/* Info banner when client views a non-reviewable bid */}
        {isClient && !canApprove && !approvalSuccess && ['draft', 'withdrawn'].includes(bid?.status) && (
          <Alert severity="info" sx={{ mb: 4 }} className="no-print">
            {bid.status === 'draft'
              ? 'This quotation is still being prepared by the contractor and has not been submitted for review yet. You will be able to approve, request changes, or reject it once the contractor submits it.'
              : 'This quotation has been withdrawn by the contractor and is no longer available for review.'}
          </Alert>
        )}

        {/* Approval Actions (Client Only, No Print) */}
        {canApprove && !approvalSuccess && (
          <Paper elevation={0} sx={{ p: 4, mb: 4 }} className="no-print">
            <Typography variant="h6" sx={{ mb: 3 }}>
              Review Actions
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<CheckCircle />}
                onClick={() => setApproveDialogOpen(true)}
                sx={{
                  background: 'linear-gradient(45deg, #4caf50 30%, #66bb6a 90%)',
                  px: 4,
                }}
              >
                Approve Quotation
              </Button>
              <Button
                variant="outlined"
                size="large"
                startIcon={<Edit />}
                onClick={() => setChangesDialogOpen(true)}
                sx={{ px: 4 }}
              >
                Request Changes
              </Button>
              <Button
                variant="outlined"
                size="large"
                color="error"
                startIcon={<Cancel />}
                onClick={() => setRejectDialogOpen(true)}
                sx={{ px: 4 }}
              >
                Reject
              </Button>
            </Box>
          </Paper>
        )}

        {/* Post-Approval Status Indicator */}
        {(approvalSuccess || bid?.status === 'accepted') && isClient && (
          <Paper elevation={0} sx={{ p: 4, mb: 4, border: '2px solid #4caf50', bgcolor: '#f1f8e9' }} className="no-print">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <CheckCircle sx={{ color: '#4caf50', fontSize: 32 }} />
              <Typography variant="h6" sx={{ color: '#2e7d32' }}>
                Bid Approved
              </Typography>
              <Chip label="Accepted" color="success" size="small" />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Project and work stages are ready. The contractor has been notified.
            </Typography>
            {targetProjectId && (
              <Button
                variant="contained"
                color="success"
                onClick={() => navigate(`/projects/${targetProjectId}`)}
                startIcon={<CheckCircle />}
              >
                View Project
              </Button>
            )}
          </Paper>
        )}

        {bid?.status === 'accepted' && isContractor && (
          <Paper elevation={0} sx={{ p: 4, mb: 4, border: '2px solid #4caf50', bgcolor: '#f1f8e9' }} className="no-print">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <CheckCircle sx={{ color: '#4caf50', fontSize: 32 }} />
              <Typography variant="h6" sx={{ color: '#2e7d32' }}>
                Your Bid Was Accepted
              </Typography>
              <Chip label="Accepted" color="success" size="small" />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              The project owner accepted this bid. Open the project to review work stages, funding status, and the next work steps.
            </Typography>
            {targetProjectId && (
              <Button
                variant="contained"
                color="success"
                onClick={() => navigate(`/projects/${targetProjectId}`)}
                startIcon={<CheckCircle />}
              >
                View Project
              </Button>
            )}
          </Paper>
        )}

        {/* Formal Contract (accepted bids, parties only) */}
        {bid?.status === 'accepted' && (isClient || isContractor) && (() => {
          const activeContract = contracts.find((c) => c.status !== 'void') || null;
          const cStatus = activeContract?.status;
          const openViewer = (contract) => { setActiveContract(contract); setContractViewerOpen(true); };
          return (
            <Paper elevation={0} sx={{ p: 4, mb: 4, border: '1px solid', borderColor: 'divider', borderRadius: 2 }} className="no-print">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
                <GavelOutlinedIcon fontSize="small" />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Contract</Typography>
                {activeContract && (
                  <Chip
                    label={(cStatus || 'draft').replaceAll('_', ' ').replace(/^\w/, (c) => c.toUpperCase())}
                    size="small"
                    color={cStatus === 'executed' ? 'success' : cStatus === 'sent' ? 'info' : 'default'}
                    sx={{ fontWeight: 600 }}
                  />
                )}
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                The formal construction contract, drawn up from this accepted quotation and signed by both parties.
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {isContractor && !activeContract && (
                  <Button variant="contained" startIcon={<DescriptionOutlinedIcon />} onClick={() => setShowContractGenerator(true)}>
                    Create contract
                  </Button>
                )}
                {isContractor && cStatus === 'draft' && (
                  <Button variant="contained" startIcon={<Edit />} onClick={() => setShowContractGenerator(true)}>
                    Resume contract draft
                  </Button>
                )}
                {isContractor && cStatus === 'sent' && (
                  <Button variant="outlined" startIcon={<DescriptionOutlinedIcon />} onClick={() => openViewer(activeContract)}>
                    Contract awaiting client
                  </Button>
                )}
                {isContractor && cStatus === 'executed' && (
                  <Button variant="contained" color="success" startIcon={<CheckCircle />} onClick={() => openViewer(activeContract)}>
                    View contract
                  </Button>
                )}

                {isClient && cStatus === 'sent' && (
                  <Button variant="contained" startIcon={<DescriptionOutlinedIcon />} onClick={() => openViewer(activeContract)}
                    sx={{ background: 'linear-gradient(45deg, #4caf50 30%, #66bb6a 90%)', px: 3 }}>
                    Review &amp; sign contract
                  </Button>
                )}
                {isClient && cStatus === 'executed' && (
                  <Button variant="contained" color="success" startIcon={<CheckCircle />} onClick={() => openViewer(activeContract)}>
                    View contract
                  </Button>
                )}
                {isClient && (!activeContract || cStatus === 'draft') && (
                  <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                    Your contractor is preparing the formal contract. You will be notified when it is ready for your signature.
                  </Typography>
                )}

                {bid?.client_signature_id && (
                  <Button variant="outlined" startIcon={<GetApp />} onClick={handleDownloadSignedQuotation}>
                    Download signed quotation
                  </Button>
                )}
              </Box>
            </Paper>
          );
        })()}

        <DocumentFooter documentType="quotation" />

        {/* Questions & Clarifications — on-the-record thread between the two
            parties, attached to this bid. Phones/emails are filtered so the
            conversation stays on the platform. */}
        {(String(user?.id) === String(bid.contractor_id) || String(user?.id) === String(bid.client_id)) && (
          <Paper elevation={1} sx={{ p: 3, mt: 3 }} className="no-print">
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
              Questions & Clarifications
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Ask about anything in this quotation — quantities, exclusions, timeline. Both sides see this thread and get notified of replies. Phone numbers and emails are removed automatically.
            </Typography>
            {comments.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 2 }}>
                No questions yet.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2, maxHeight: 360, overflowY: 'auto' }}>
                {comments.map((comment) => {
                  const isMine = String(comment.user_id) === String(user?.id);
                  const authorName = comment.company_name ||
                    [comment.first_name, comment.last_name].filter(Boolean).join(' ') ||
                    (isMine ? 'You' : 'Other party');
                  return (
                    <Box
                      key={comment.id}
                      sx={{
                        alignSelf: isMine ? 'flex-end' : 'flex-start',
                        maxWidth: '85%',
                        bgcolor: isMine ? 'primary.50' : 'grey.100',
                        borderRadius: 2,
                        px: 2,
                        py: 1
                      }}
                    >
                      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
                        {isMine ? 'You' : authorName}
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1, fontWeight: 400 }}>
                          {comment.created_at ? new Date(comment.created_at).toLocaleString() : ''}
                        </Typography>
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{comment.comment_text}</Typography>
                    </Box>
                  );
                })}
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                size="small"
                multiline
                maxRows={4}
                placeholder="Ask a question or clarify something about this bid…"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
              />
              <Button
                variant="contained"
                endIcon={<Send />}
                disabled={postingComment || !newComment.trim()}
                onClick={handlePostComment}
                sx={{ flexShrink: 0 }}
              >
                Send
              </Button>
            </Box>
          </Paper>
        )}

        {/* Approve Dialog */}
        <Dialog open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogCloseButton onClick={() => setApproveDialogOpen(false)} />
          <DialogTitle>Approve Quotation</DialogTitle>
          <DialogContent>
            <Typography variant="body1" paragraph>
              Are you sure you want to approve this quotation?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This will:
            </Typography>
            <Box component="ul" sx={{ mt: 1 }}>
              <Typography component="li" variant="body2" color="text.secondary">Create a new project</Typography>
              <Typography component="li" variant="body2" color="text.secondary">Set up work stages from the payment schedule</Typography>
              <Typography component="li" variant="body2" color="text.secondary">Notify the contractor</Typography>
            </Box>
            <Box sx={{ mt: 2, p: 1.5, bgcolor: 'info.50', borderRadius: 1, border: '1px solid', borderColor: 'info.200' }}>
              <Typography variant="body2" color="info.dark">
                <strong>Note:</strong> On the next step you will sign the quotation. Your signature accepts these exact terms and creates a binding record. You then deposit the amount into your project wallet — it stays yours and is only released as you approve each stage. That&apos;s the Eqaya Guarantee.
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setApproveDialogOpen(false)} disabled={actionLoading}>Cancel</Button>
            <Button
              onClick={() => { setApproveDialogOpen(false); setSignatureModalOpen(true); }}
              variant="contained"
              color="success"
              disabled={actionLoading}
              startIcon={<CheckCircle />}
            >
              Continue to sign
            </Button>
          </DialogActions>
        </Dialog>

        {/* Client signs the quotation to accept it */}
        <DigitalSignatureCapture
          open={signatureModalOpen}
          onClose={() => { if (!actionLoading) setSignatureModalOpen(false); }}
          onSignatureComplete={handleAcceptWithSignature}
          documentType="bid"
          documentId={bid.id}
          documentData={bid}
          signerName={`${user?.profile?.firstName || user?.first_name || ''} ${user?.profile?.lastName || user?.last_name || ''}`.trim() || user?.email || 'Client'}
          signerEmail={user?.email || ''}
          consentText={signatureService.getConsentText('bid')}
        />

        {/* Reject Dialog */}
        <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogCloseButton onClick={() => setRejectDialogOpen(false)} />
          <DialogTitle>Reject Quotation</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" paragraph>
              Please provide a reason for rejecting this bid:
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="e.g., Cost is too high, timeline doesn't work, etc."
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRejectDialogOpen(false)} disabled={actionLoading}>Cancel</Button>
            <Button
              onClick={handleRejectBid}
              variant="contained"
              color="error"
              disabled={actionLoading}
              startIcon={actionLoading ? <CircularProgress size={20} /> : <Cancel />}
            >
              Reject
            </Button>
          </DialogActions>
        </Dialog>

        {/* Request Changes Dialog */}
        <Dialog open={changesDialogOpen} onClose={() => setChangesDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogCloseButton onClick={() => setChangesDialogOpen(false)} />
          <DialogTitle>Request Changes</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" paragraph>
              Describe the changes you would like the contractor to make:
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={requestedChanges}
              onChange={(e) => setRequestedChanges(e.target.value)}
              placeholder="e.g., Please provide more detail on the timeline, adjust the cost breakdown, etc."
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setChangesDialogOpen(false)} disabled={actionLoading}>Cancel</Button>
            <Button
              onClick={handleRequestChanges}
              variant="contained"
              disabled={actionLoading}
              startIcon={actionLoading ? <CircularProgress size={20} /> : <Edit />}
            >
              Request Changes
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Bid Modal */}
        <AIBidGenerator
          open={showEditModal}
          onClose={() => setShowEditModal(false)}
          projectId={bid.project_id}
          clientId={bid.client_id}
          existingBid={bid}
          onBidCreated={(updatedBid) => {
            setShowEditModal(false);
            loadBid();
            if (onUpdate) onUpdate(updatedBid);
          }}
        />

        {/* Contract creation wizard (contractor) */}
        {showContractGenerator && (
          <AIContractGenerator
            open={showContractGenerator}
            onClose={() => setShowContractGenerator(false)}
            bid={bid}
            existingContract={contracts.find((c) => c.status === 'draft') || undefined}
            onChanged={loadContracts}
          />
        )}

        {/* Contract review / signing / void */}
        {contractViewerOpen && activeContract && (
          <ContractViewer
            open={contractViewerOpen}
            onClose={() => setContractViewerOpen(false)}
            contract={activeContract}
            bid={bid}
            user={user}
            onChanged={loadContracts}
          />
        )}

        {/* Print Styles */}
        <style>
          {`
            @media print {
              .no-print {
                display: none !important;
              }
              body {
                background: white;
              }
              .bid-detail-content {
                margin: 0;
                padding: 20px;
              }
            }
          `}
        </style>
      </div>

      {/* Snackbar for approval feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === 'error' ? 10000 : 5000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%', maxWidth: 600 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default BidDetailView;
